import { Graphics } from 'pixi.js';
import type { StructureDef } from '@playnets/sim';

/**
 * Structures are drawn pointing outward (negative y) so a sprite rotated to
 * its slot bearing aims away from the crust. Flat fills, thick linework, no
 * gradients — same print as everything else.
 *
 * Everything here sits on a mounting pad and is roughly twice the size it was
 * originally. Playtesting found the hardware you spend your juice on was the
 * least visible thing on the board, which is a bad joke on the player. A dark
 * outline keeps it legible against six different region grounds — the same rule
 * the enemies follow.
 *
 * The ladder is five rungs deep now, and it has to *read* at five rungs or
 * there was no point deepening it. Three things grow together: the hardware
 * gets physically bigger, it gains a barrel (up to three — past that they stop
 * being countable and start being a smudge), and a row of pips underneath spells
 * out the exact rung. Merge results get a heavier pad and a halo, because they
 * are a different class of object rather than a taller version of one.
 */
export function drawStructure(g: Graphics, def: StructureDef, tier: number, spin: number): void {
  const c = def.colour;
  const top = Math.max(def.tiers.length - 1, 1);
  /** 0 at tier 0, 1 at the top of whatever ladder this thing has. */
  const up = tier / top;
  const apex = def.mergeOnly === true;
  /**
   * Physical growth across the ladder. A merge result starts above where its
   * source topped out — it costs a finished ladder plus a gun, and arriving
   * visibly *smaller* than the thing you fed in reads as a downgrade no matter
   * what the numbers say.
   */
  const k = (apex ? 1.55 : 1) + up * 0.45;
  const barrels = Math.min(tier + (apex ? 3 : 1), 3);

  // A mounting pad under everything. It gives the hardware a footprint you can
  // find at a glance on a phone, and it reads as bolted to the crust rather
  // than floating on it — which is what a surface mount is.
  if (def.kind !== 'moon') {
    const pad = (apex ? 20 : 16) * (1 + up * 0.22);
    if (apex) {
      // The halo is the whole tell: you earned this one.
      g.circle(0, 2, pad + 7).fill({ color: c, alpha: 0.14 });
      g.circle(0, 2, pad + 7).stroke({ width: 1.5, color: c, alpha: 0.45 });
    }
    g.circle(0, 2, pad).fill({ color: 0x11202c, alpha: 0.4 });
    g.circle(0, 2, pad).stroke({ width: apex ? 3.5 : 2.5, color: c, alpha: 0.75 });
  }

  switch (def.kind) {
    case 'turret': {
      g.rect(-15 * k, -9 * k, 30 * k, 21 * k).fill({ color: c });
      for (let i = 0; i < barrels; i++) {
        const w = 7 * k;
        g.rect(-w / 2 + (i - (barrels - 1) / 2) * 10 * k, -32 * k, w, 25 * k).fill({ color: c });
      }
      // Apex turrets carry a shoulder block either side, so the silhouette
      // differs at a glance and not just in size.
      if (apex) {
        g.rect(-24 * k, -4 * k, 9 * k, 14 * k).fill({ color: c, alpha: 0.9 });
        g.rect(15 * k, -4 * k, 9 * k, 14 * k).fill({ color: c, alpha: 0.9 });
      }
      break;
    }

    case 'beam':
      g.poly([-12 * k, 9 * k, 12 * k, 9 * k, 6 * k, -36 * k, -6 * k, -36 * k]).fill({ color: c });
      g.circle(0, 4, (7 + tier * 1.6) * k).fill({ color: c, alpha: 0.85 });
      break;

    case 'moon': {
      // Mass made visible: bigger moon, more craters, slower visible spin.
      const r = 21 + (1 + up * 2.2) * 7;
      g.circle(0, 0, r).fill({ color: c });
      const craters = 3 + Math.round(up * 4);
      for (let i = 0; i < craters; i++) {
        const a = spin + (i / craters) * Math.PI * 2;
        g.circle(Math.cos(a) * r * 0.45, Math.sin(a) * r * 0.45, r * 0.17).fill({
          color: 0x5b616d,
          alpha: 0.75,
        });
      }
      break;
    }

    case 'mine': {
      // A charge per rung, fanned outward — the count is the stat.
      const n = Math.min(tier + 3, 7);
      for (let i = 0; i < n; i++) {
        const a = -Math.PI / 2 + (i - (n - 1) / 2) * 0.42;
        g.circle(Math.cos(a) * 19 * k, Math.sin(a) * 19 * k, 6.5).fill({ color: c });
      }
      g.circle(0, 2, 8 * k).fill({ color: c, alpha: 0.7 });
      break;
    }

    case 'support':
      g.poly([0, -29 * k, 16 * k, 0, 0, 16 * k, -16 * k, 0]).fill({ color: c, alpha: 0.95 });
      g.circle(0, -4, (6 + tier * 1.6) * k).fill({ color: c });
      break;

    case 'drone-bay':
      g.rect(-19 * k, -12 * k, 38 * k, 25 * k).fill({ color: c });
      g.rect(-10 * k, -24 * k, 8 * k, 14 * k).fill({ color: c, alpha: 0.75 });
      g.rect(2 * k, -24 * k, 8 * k, 14 * k).fill({ color: c, alpha: 0.75 });
      break;
  }

  // One confident outline over everything drawn above.
  g.stroke({ width: 2, color: 0x11202c, alpha: 0.5 });

  // Tier pips, read at a glance without a label. One row of up to five; a
  // merge result shows a filled bar instead, because "which rung" matters less
  // than "this is the other thing".
  const pips = tier + 1;
  const gap = pips > 3 ? 6.5 : 8;
  for (let i = 0; i < pips; i++) {
    g.circle((i - (pips - 1) / 2) * gap, 25 * k, 2.6).fill({
      color: apex ? c : 0xf2e9dc,
      alpha: 0.95,
    });
  }
}
